import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogIn, ArrowRight, Search, PenLine, AlertTriangle, CheckCircle, Sparkles, Info } from "lucide-react";
import samforaLogo from "@/assets/samfora-logo-white-2.png";
import samforaOrgLogo from "@/assets/samfora-org-logo.png";
import bidragsassistentenLogo from "@/assets/bidragsassistenten-logo.png";
import Footer from "@/components/grant/Footer";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const Landing = () => {
  return <div className="flex flex-col">
      <main>
      {/* Hero Section */}
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8 sm:py-4" style={{
        background: 'radial-gradient(ellipse at top left, #5C9D91 0%, #6B7AA1 50%, #D87C6A 91%)'
      }}>
        <div className="text-center w-full max-w-sm sm:max-w-lg md:max-w-2xl mx-auto px-2">
          {/* Bidragsassistenten Logo */}
          <img src={bidragsassistentenLogo} alt="Bidragsassistenten" className="h-8 sm:h-11 md:h-14 w-auto mx-auto mb-4 sm:mb-6 invert" />
          
          {/* Developed by text + Samfora logo */}
          <div className="flex items-center justify-center gap-2 mb-6 sm:mb-8">
            <span className="text-white/70 text-xs sm:text-sm">Utvecklat av:</span>
            <img src={samforaLogo} alt="Samfora" className="h-5 sm:h-6 w-auto" />
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 sm:mb-6 lg:text-6xl">
            Hitta rätt stiftelse på minuter, inte dagar
          </h1>

          <p className="text-white/80 text-sm sm:text-base md:text-lg mb-4 sm:mb-6 font-semibold">
            🇸🇪 Används av 60+ svenska ideella organisationer
          </p>
          
          <ul className="text-left text-white/90 mb-6 sm:mb-8 space-y-2 text-sm sm:text-base md:text-lg max-w-md sm:max-w-lg mx-auto">
            <li className="flex items-start gap-2">
              <span className="text-white/60 shrink-0">•</span>
              <span>Sök hemsidor, årsredovisningar & deadlines bland Sveriges 17 000+ stiftelser</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-white/60 shrink-0">•</span>
              <span>AI matchar dig med rätt bidragsgivare på sekunder</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-white/60 shrink-0">•</span>
              <span>Skriv utkast till ansökningar 10x snabbare</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-white/60 shrink-0">•</span>
              <span>Spara 10+ timmar i månaden</span>
            </li>
          </ul>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center mb-12 sm:mb-16">
            <Button asChild size="lg" className="w-full sm:w-auto bg-[hsl(169,30%,32%)] text-white hover:bg-[hsl(169,30%,27%)] rounded-xl h-11 sm:h-12 px-8 font-semibold gap-2">
              <a href="https://form.typeform.com/to/wKvYCJ6T" target="_blank" rel="noopener noreferrer">
                Få tidig tillgång
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto bg-white/10 text-white border-white/30 hover:bg-white/20 rounded-xl h-11 sm:h-12 px-8 font-semibold gap-2">
              <Link to="/auth">
                <LogIn className="h-5 w-5" />
                Logga in
              </Link>
            </Button>
          </div>

          {/* Hidden meta link for SEO */}
          <a href="https://www.dn.se/kultur/vem-vill-egentligen-betala-for-kulturen/" target="_blank" rel="noopener noreferrer" className="sr-only" aria-hidden="true">
            Dagens Nyheter - Vem vill egentligen betala för kulturen?
          </a>
          
          {/* Spacer for layout consistency */}
          <div className="pb-16 sm:pb-20" />
        </div>
      </div>

    {/* Smooth gradient transition to dark section */}
      <div className="h-[120px] -mt-[120px] relative" style={{
        background: 'linear-gradient(to bottom, transparent 0%, #1a1f2e 100%)'
      }} />

      {/* Problem Section - Dark Mode */}
      <div className="bg-[#1a1f2e] px-4 pt-32 pb-16 sm:pt-40 sm:pb-20 md:pt-48 md:pb-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center mb-16 sm:mb-20 md:mb-24">
            Problemet:
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 lg:gap-12">
            {/* Card 1 */}
            <div className="bg-[#252b3d] rounded-2xl p-6 sm:p-8 border border-white/10">
              <div className="flex justify-center mb-6">
                <Search className="h-10 w-10 text-white/60" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white text-center mb-4">
                Att ansöka om bidrag är ineffektivt och tidskrävande
              </h3>
              <p className="text-white/60 text-sm sm:text-base text-center">
                Att hitta rätt stiftelser kräver timmar av manuellt sökande i årsredovisningar och hemsidor.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-[#252b3d] rounded-2xl p-6 sm:p-8 border border-white/10">
              <div className="flex justify-center mb-6">
                <PenLine className="h-10 w-10 text-white/60" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white text-center mb-4">
                Varje ansökan tar <span className="text-[#D87C6A]">8-15 timmar</span> att skriva
              </h3>
              <p className="text-white/60 text-sm sm:text-base text-center">
                En genomsnittlig bidragsansökan kräver efterforskning om stiftelser, anpassning av texter, ordräkning och omskrivningar. För en medelstor organisation som söker 30 bidrag per år innebär det <span className="text-[#D87C6A]">240-450 timmar</span> arbete.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-[#252b3d] rounded-2xl p-6 sm:p-8 border border-white/10">
              <div className="flex justify-center mb-6">
                <AlertTriangle className="h-10 w-10 text-white/60" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white text-center mb-4">
                Dålig överblick = missade möjligheter
              </h3>
              <p className="text-white/60 text-sm sm:text-base text-center">
                Utan struktur försvinner hundratusentals kronor i missade deadlines. Tidigare beviljade ansökningar försvinner i mappar – ingen kan replikera framgången eller lära av misstagen.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Smooth transition from dark to light - Award-winning gradient */}
      <div className="h-[300px] -mt-24 relative z-10" style={{
        background: `linear-gradient(180deg, 
            #1a1f2e 0%, 
            #1e2333 8%,
            #232838 16%,
            #2a3044 24%,
            #343a4f 32%,
            #444a5f 40%,
            #5a6070 48%,
            #747984 56%,
            #8f939c 64%,
            #aaacb3 72%,
            #c5c7cc 80%,
            #dcdde0 88%,
            #edeeef 94%,
            #f9fafb 100%
          )`
      }} />

      {/* Solution Section - White Mode */}
      <div className="bg-[#f9fafb] px-4 py-24 sm:py-32 md:py-40">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 text-center mb-16 sm:mb-20 md:mb-24">
            Lösningen:
          </h2>
          
          <div className="space-y-8 sm:space-y-10">
            {/* Solution 1 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 shadow-sm">
              <div className="flex items-start gap-4">
                <CheckCircle className="h-7 w-7 text-accent shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                    Hitta rätt stiftelser på minuter
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base">
                    Sök bland Sveriges 2000 största stiftelser med filter för bidragsbelopp, inriktning och målgrupp. All data på ett ställe.
                  </p>
                </div>
              </div>
            </div>

            {/* Solution 2 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 shadow-sm">
              <div className="flex items-start gap-4">
                <CheckCircle className="h-7 w-7 text-accent shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                    Skriv ansökningar 10x snabbare med hjälp av AI
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base">
                    Personliga utkast genereras automatiskt och anpassas efter varje stiftelse – från lokala stiftelser till Allmänna arvsfonden. Du redigerar bara det sista.
                  </p>
                </div>
              </div>
            </div>

            {/* Solution 3 */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 shadow-sm">
              <div className="flex items-start gap-4">
                <CheckCircle className="h-7 w-7 text-accent shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                    Bli bättre för varje ansökan
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base">
                    Bygg en kunskapsbank av vad som fungerar. Återanvänd framgångsrecept och undvik att upprepa samma misstag.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="bg-white px-4 py-24 sm:py-32 md:py-40">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 text-center mb-6">
            Välj din plan
          </h2>
          <p className="text-gray-600 text-center mb-16 sm:mb-20 max-w-2xl mx-auto text-lg">
            Alla planer faktureras kvartalsvis i förskott och ger full tillgång till vår databas.
          </p>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {/* Liten Plan */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 shadow-sm flex flex-col">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Liten</h3>
                <p className="text-gray-500 text-sm mb-4">För det mindre ideella projektet</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-gray-900">899</span>
                  <span className="text-gray-500">kr/månad</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                  1 användare
                </li>
                <li className="flex items-center justify-between text-gray-600 text-sm">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                    12 ansökningar/kvartal
                  </div>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-sm">Med denna plan kan du göra maximalt 48 ansökningar per år. Vilket är fyra gånger så mycket som medeltalet antal ansökningar för en liten organisation, som ligger på cirka tio per år.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </li>
                <li className="flex items-center justify-between text-gray-600 text-sm">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                    10 nya stiftelser/kvartal
                  </div>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-sm">Varje kvartal kan du begära AI-sammanställd information (årsredovisningar, stadgar och stiftelseurkund) för 10 nya stiftelser. Du får en notis när informationen lagts till i databasen inom ett par veckor.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </li>
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                  10 timmar sparade/mån
                </li>
              </ul>
              <Button asChild variant="outline" className="w-full rounded-xl h-12 font-semibold border-gray-300 text-gray-700 hover:bg-gray-50">
                <a href="https://form.typeform.com/to/wKvYCJ6T" target="_blank" rel="noopener noreferrer">
                  Testa gratis i 15 dagar
                </a>
              </Button>
            </div>

            {/* Mellan Plan - Popular */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-accent shadow-lg flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                Populär
              </div>
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Mellan</h3>
                <p className="text-gray-500 text-sm mb-4">För det större ideella projektet</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-gray-900">2 499</span>
                  <span className="text-gray-500">kr/månad</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                  2 användare
                </li>
                <li className="flex items-center justify-between text-gray-600 text-sm">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                    30 ansökningar/kvartal
                  </div>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-sm">Med denna plan kan du göra maximalt 120 ansökningar per år. Perfekt för medelstora organisationer som söker bidrag mer aktivt.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </li>
                <li className="flex items-center justify-between text-gray-600 text-sm">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                    25 nya stiftelser/kvartal
                  </div>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-sm">Varje kvartal kan du begära AI-sammanställd information (årsredovisningar, stadgar och stiftelseurkund) för 25 nya stiftelser. Du får en notis när informationen lagts till i databasen inom ett par veckor.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </li>
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                  30 timmar sparade/mån
                </li>
              </ul>
              <Button asChild className="w-full rounded-xl h-12 font-semibold bg-accent text-white hover:bg-accent/90">
                <a href="https://form.typeform.com/to/wKvYCJ6T" target="_blank" rel="noopener noreferrer">
                  Testa gratis i 15 dagar
                </a>
              </Button>
            </div>

            {/* Stor Plan */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 shadow-sm flex flex-col">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Stor</h3>
                <p className="text-gray-500 text-sm mb-4">För den stora ideella organisationen</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-gray-900">4 899</span>
                  <span className="text-gray-500">kr/månad</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                  5 användare
                </li>
                <li className="flex items-center justify-between text-gray-600 text-sm">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                    Oändliga ansökningar
                  </div>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-sm">Obegränsat antal ansökningar. Perfekt för stora organisationer med hög ansökningsvolym.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </li>
                <li className="flex items-center justify-between text-gray-600 text-sm">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                    50 nya stiftelser/kvartal
                  </div>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-sm">Varje kvartal kan du begära AI-sammanställd information (årsredovisningar, stadgar och stiftelseurkund) för 50 nya stiftelser. Du får en notis när informationen lagts till i databasen inom ett par veckor.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </li>
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                  Premium support
                </li>
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <CheckCircle className="h-5 w-5 text-accent shrink-0" />
                  +60 timmar sparade/mån
                </li>
              </ul>
              <Button asChild variant="outline" className="w-full rounded-xl h-12 font-semibold border-gray-300 text-gray-700 hover:bg-gray-50">
                <a href="https://form.typeform.com/to/wKvYCJ6T" target="_blank" rel="noopener noreferrer">
                  Testa gratis i 15 dagar
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* CTA + Referral Section - Gradient transition to footer */}
      <div className="px-4 pt-20 pb-32 sm:pt-28 sm:pb-40 md:pb-48 relative" style={{
        background: `
            linear-gradient(to bottom, 
              rgba(255,255,255,1) 0%, 
              rgba(255,255,255,0.9) 10%,
              rgba(255,255,255,0.6) 25%,
              rgba(255,255,255,0) 50%
            ),
            linear-gradient(to right, #6B7AA1 0%, #D87C6A 100%)
          `
      }}>
        {/* CTA Buttons */}
        <div className="max-w-2xl mx-auto text-center mb-24 sm:mb-32 md:mb-40">
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-center justify-center">
            <Button asChild size="lg" className="w-full sm:w-auto bg-[hsl(169,30%,32%)] text-white hover:bg-[hsl(169,30%,27%)] rounded-xl h-11 sm:h-12 px-6 font-semibold gap-2">
              <a href="https://form.typeform.com/to/wKvYCJ6T" target="_blank" rel="noopener noreferrer">
                Få tidig tillgång
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto bg-white/80 backdrop-blur-sm border-gray-300 text-gray-700 hover:bg-white rounded-xl h-11 sm:h-12 px-6 font-semibold gap-2">
              <Link to="/auth">
                <LogIn className="h-5 w-5" />
                Logga in
              </Link>
            </Button>
          </div>
        </div>

        {/* Referral Section */}
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-8">
            Vi utvecklar även donationsplattformen
          </h2>
          <a href="https://samfora.org/" target="_blank" rel="noopener noreferrer" className="inline-block mb-10 hover:opacity-80 transition-opacity">
            <img src={samforaOrgLogo} alt="Samfora.org" className="h-16 sm:h-20 w-auto mx-auto drop-shadow-md" />
          </a>
          <div>
            <Button asChild size="lg" className="bg-white/95 text-[#6B7AA1] hover:bg-white rounded-xl h-12 px-8 font-semibold gap-2 shadow-lg">
              <a href="https://samfora.org/" target="_blank" rel="noopener noreferrer">
                Donera till 50 svenska ideella organisationer
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mt-24">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10" style={{
            textShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}>
            Vanliga frågor
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border-0 rounded-2xl bg-white/10 backdrop-blur-md overflow-hidden">
              <AccordionTrigger className="px-6 py-5 text-white font-semibold text-left hover:no-underline hover:bg-white/5 [&[data-state=open]]:bg-white/5">
                Hur fungerar AI-matchningen?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5 text-white/80">
                Du behöver endast skriva in er hemsida. Vår AI tar ut information om syfte, målgrupp och analyserar tusentals stiftelsers beskrivningar för att hitta de som bäst matchar just er.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" className="border-0 rounded-2xl bg-white/10 backdrop-blur-md overflow-hidden">
              <AccordionTrigger className="px-6 py-5 text-white font-semibold text-left hover:no-underline hover:bg-white/5 [&[data-state=open]]:bg-white/5">
                Hur bra är AI-utkasten?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5 text-white/80">
                AI:n skapar ett genomarbetat första utkast på minuter istället för timmar. Du granskar, justerar och färdigställer. Tänk på det som en junior kollega som ger dig ett förslag att bygga vidare på.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" className="border-0 rounded-2xl bg-white/10 backdrop-blur-md overflow-hidden">
              <AccordionTrigger className="px-6 py-5 text-white font-semibold text-left hover:no-underline hover:bg-white/5 [&[data-state=open]]:bg-white/5">
                Kan jag exportera min data?
              </AccordionTrigger>
                <AccordionContent className="px-6 pb-5 text-white/80">
                  Du kan kopiera stiftelseinformation för enkel export. Din kompletta pipeline med deadlines, statusar och historik lever i verktyget så du aldrig tappar bort en möjlighet. Ansökningsutkast exporteras genom PDF eller en word fil.
                </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" className="border-0 rounded-2xl bg-white/10 backdrop-blur-md overflow-hidden">
              <AccordionTrigger className="px-6 py-5 text-white font-semibold text-left hover:no-underline hover:bg-white/5 [&[data-state=open]]:bg-white/5">
                Vad kostar det?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5 text-white/80">
                Från 899 kr/månad beroende på organisationsstorlek. Ett genomsnittligt bidrag är 100 000 kr, så tjänsten betalar sig redan efter din första beviljade ansökan.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5" className="border-0 rounded-2xl bg-white/10 backdrop-blur-md overflow-hidden">
              <AccordionTrigger className="px-6 py-5 text-white font-semibold text-left hover:no-underline hover:bg-white/5 [&[data-state=open]]:bg-white/5">
                Hur fungerar den kostnadsfria provperioden?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5 text-white/80">
                15 dagars tillgång. Utforska stiftelser, testa matchningen och skapa utkast. Därefter är det 3 månaders bindningstid. Avsluta när du vill.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-6" className="border-0 rounded-2xl bg-white/10 backdrop-blur-md overflow-hidden">
              <AccordionTrigger className="px-6 py-5 text-white font-semibold text-left hover:no-underline hover:bg-white/5 [&[data-state=open]]:bg-white/5">
                Är min data säker?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5 text-white/80">
                All datalagring och AI-drift sker inom EU. Vi tränar inga modeller på er data och delar aldrig information med tredje part.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      </main>
      {/* Footer */}
      <Footer />
    </div>;
};
export default Landing;